'use client';

import { useEffect, useState } from 'react';
import { InstagramGallery, type InstagramGalleryItem } from './instagram-gallery';

interface PublicGalleryProps {
  channel: 'site' | 'marketplace';
  /** Ordering-site handle/custom domain or marketplace slug, never an owner business ID. */
  handle: string;
  compact?: boolean;
  className?: string;
}

function isMedia(value: unknown): value is InstagramGalleryItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return ['id', 'mediaType', 'postedAt', 'username'].every((key) => typeof item[key] === 'string')
    && ['caption', 'previewUrl', 'permalink'].every((key) => item[key] === null || typeof item[key] === 'string');
}

function GalleryRequest({ path, compact, className }: {
  path: string; compact?: boolean; className?: string;
}) {
  const [media, setMedia] = useState<InstagramGalleryItem[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        // Anonymous and uncached: site visitors need neither app auth nor a selected business.
        const response = await fetch(path, {
          cache: 'no-store', credentials: 'omit', redirect: 'error', signal: controller.signal,
        });
        const body = response.ok ? await response.json() : null;
        const items = Array.isArray(body?.media) ? body.media.filter(isMedia) : [];
        if (!controller.signal.aborted && items.length) setMedia(items);
      } catch {
        // Each keyed request starts empty; failures leave the working menu alone.
      }
    })();
    return () => controller.abort();
  }, [path]);

  if (!media.length) return null;
  return <div className={className}><InstagramGallery items={media} compact={compact} /></div>;
}

export function PublicInstagramGallery({ channel, handle, ...props }: PublicGalleryProps) {
  if (!handle) return null;
  const path = `/api/proxy/storefront/${channel === 'site' ? 'site/' : ''}${encodeURIComponent(handle)}/instagram`;
  // Reset synchronously on tenant/channel changes, including navigating A → B → A.
  return <GalleryRequest key={path} path={path} {...props} />;
}
